/**
 * FeatureGrid セクション。3 variants:
 *   icons_3col   — 3 列アイコン+見出し+説明
 *   icons_4col   — 4 列アイコン+見出し+説明
 *   numbered     — 番号付きフィーチャー (大きめ番号 → 見出し → 説明)
 */
import {
  Award,
  Box,
  CheckCircle2,
  Cloud,
  Cpu,
  Globe,
  Grid3x3,
  Heart,
  Layers,
  Lock,
  Rocket,
  Settings,
  Shield,
  Sparkles,
  Star,
  Target,
  TrendingUp,
  Users,
  Zap,
  type LucideIcon,
} from 'lucide-react';

import { ArrayField, SelectField, TextField, TextareaField } from './primitives';
import type { EditorProps, RendererProps, SectionDefinition } from './types';

const ICON_MAP: Record<string, LucideIcon> = {
  sparkles: Sparkles,
  zap: Zap,
  shield: Shield,
  rocket: Rocket,
  cloud: Cloud,
  lock: Lock,
  cpu: Cpu,
  globe: Globe,
  star: Star,
  heart: Heart,
  award: Award,
  users: Users,
  layers: Layers,
  box: Box,
  settings: Settings,
  target: Target,
  'trending-up': TrendingUp,
  'check-circle': CheckCircle2,
};

const ICON_OPTIONS = Object.keys(ICON_MAP).map((k) => ({ value: k, label: k }));

interface FeatureItem {
  icon: string;
  title: string;
  description: string;
}
interface FeatureGridConfig {
  title: string;
  subtitle: string;
  items: FeatureItem[];
}

function FeatureGridEditor({ config, onChange, onCommit }: EditorProps) {
  const cfg = config as unknown as FeatureGridConfig;
  const set = (patch: Partial<FeatureGridConfig>) =>
    onChange({ ...config, ...patch });
  return (
    <div className="space-y-3">
      <TextField
        label="セクション見出し"
        value={cfg.title ?? ''}
        onChange={(v) => set({ title: v })}
        onCommit={onCommit}
      />
      <TextField
        label="サブタイトル"
        value={cfg.subtitle ?? ''}
        onChange={(v) => set({ subtitle: v })}
        onCommit={onCommit}
      />
      <ArrayField<FeatureItem>
        label="特徴リスト"
        items={cfg.items ?? []}
        onChange={(next) => set({ items: next })}
        emptyItem={() => ({ icon: 'sparkles', title: '', description: '' })}
        renderItem={(item, _i, update) => (
          <div className="space-y-2">
            <SelectField
              label="アイコン"
              value={item.icon}
              onChange={(v) => update({ icon: v })}
              onCommit={onCommit}
              options={ICON_OPTIONS}
            />
            <TextField
              label="見出し"
              value={item.title}
              onChange={(v) => update({ title: v })}
              onCommit={onCommit}
            />
            <TextareaField
              label="説明"
              value={item.description}
              onChange={(v) => update({ description: v })}
              onCommit={onCommit}
              rows={2}
            />
          </div>
        )}
      />
    </div>
  );
}

function FeatureItemCard({
  item,
  numbered,
  index,
}: {
  item: FeatureItem;
  numbered?: boolean;
  index?: number;
}) {
  const Icon = ICON_MAP[item.icon] ?? Sparkles;
  return (
    <div className="text-left p-5 rounded-lg border border-[var(--border)] bg-[var(--bg-elevated)]">
      {numbered ? (
        <div className="text-3xl font-bold text-[var(--accent)] mb-2 leading-none">
          {String((index ?? 0) + 1).padStart(2, '0')}
        </div>
      ) : (
        <Icon className="size-8 text-[var(--accent)] mb-3" />
      )}
      <h4 className="font-semibold mb-1.5">{item.title}</h4>
      <p className="text-sm text-[var(--text-secondary)] leading-relaxed">
        {item.description}
      </p>
    </div>
  );
}

function GridRenderer(cols: number) {
  return function Render({ config }: RendererProps) {
    const v = config as unknown as FeatureGridConfig;
    const items = v.items ?? [];
    return (
      <section>
        {(v.title || v.subtitle) && (
          <div className="text-center mb-8">
            {v.title && <h3 className="text-2xl font-bold mb-1">{v.title}</h3>}
            {v.subtitle && (
              <p className="text-[var(--text-secondary)]">{v.subtitle}</p>
            )}
          </div>
        )}
        <div
          className={`grid gap-4 grid-cols-1 sm:grid-cols-2 ${
            cols === 4 ? 'lg:grid-cols-4' : 'lg:grid-cols-3'
          }`}
        >
          {items.map((it, i) => (
            <FeatureItemCard key={i} item={it} />
          ))}
        </div>
      </section>
    );
  };
}

function NumberedRenderer({ config }: RendererProps) {
  const v = config as unknown as FeatureGridConfig;
  const items = v.items ?? [];
  return (
    <section>
      {(v.title || v.subtitle) && (
        <div className="text-center mb-8">
          {v.title && <h3 className="text-2xl font-bold mb-1">{v.title}</h3>}
          {v.subtitle && (
            <p className="text-[var(--text-secondary)]">{v.subtitle}</p>
          )}
        </div>
      )}
      <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((it, i) => (
          <FeatureItemCard key={i} item={it} numbered index={i} />
        ))}
      </div>
    </section>
  );
}

const sample3 = (): Record<string, unknown> => ({
  title: '選ばれる理由',
  subtitle: 'お客様に選ばれる 3 つの強み',
  items: [
    {
      icon: 'rocket',
      title: '導入スピード',
      description: '最短 3 営業日で運用開始。複雑な設定は不要です。',
    },
    {
      icon: 'shield',
      title: 'セキュリティ',
      description: 'SOC2 / ISO 27001 取得。安心してご利用いただけます。',
    },
    {
      icon: 'users',
      title: 'サポート体制',
      description: '専任のカスタマーサクセスが導入から運用まで伴走します。',
    },
  ] as FeatureItem[],
});

const sample4 = (): Record<string, unknown> => ({
  title: 'プラットフォームの特長',
  subtitle: '',
  items: [
    {
      icon: 'cloud',
      title: 'クラウドネイティブ',
      description: '高可用性とスケーラビリティを両立。',
    },
    {
      icon: 'cpu',
      title: '高速処理',
      description: '最新のインフラで安定したパフォーマンスを提供。',
    },
    {
      icon: 'globe',
      title: 'グローバル対応',
      description: '世界各地の拠点から低レイテンシでアクセス。',
    },
    {
      icon: 'lock',
      title: 'エンタープライズ品質',
      description: 'SAML / SCIM / 監査ログまで標準対応。',
    },
  ] as FeatureItem[],
});

export const featureGridDefinition: SectionDefinition = {
  type: 'feature_grid',
  label: '特徴グリッド',
  icon: Grid3x3,
  description: 'アイコン+見出し+説明を並べて訴求するグリッド。',
  defaultVariant: 'icons_3col',
  useCases: ['service', 'b2b_lease', 'platform', 'physical_product'],
  variants: {
    icons_3col: {
      label: 'アイコン 3 列',
      description: '3 列のアイコンカード',
      Renderer: GridRenderer(3),
      Editor: FeatureGridEditor,
      sample: sample3,
    },
    icons_4col: {
      label: 'アイコン 4 列',
      description: '4 列のコンパクトカード',
      Renderer: GridRenderer(4),
      Editor: FeatureGridEditor,
      sample: sample4,
    },
    numbered: {
      label: '番号付き',
      description: '01 / 02 / 03 の番号で訴求',
      Renderer: NumberedRenderer,
      Editor: FeatureGridEditor,
      sample: sample3,
    },
  },
};
